
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

interface DocuSealEmbeddedProps {
  signingUrl: string;
  onComplete?: () => void;
  height?: string | number;
}

export const DocuSealEmbedded: React.FC<DocuSealEmbeddedProps> = ({ 
  signingUrl, 
  onComplete,
  height = '80vh' 
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState<number | null>(null);
  
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // DocuSeal sends a message when the signing is complete
      if (event.data === 'docuseal:submission_completed') {
        console.log('DocuSeal: Signing completed');
        if (onComplete) {
          onComplete();
        }
      } else if (event.data === 'docuseal:loaded') {
        console.log('DocuSeal: Loaded successfully');
        setLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Set a timeout to hide the loader after a reasonable time
    // even if DocuSeal doesn't fully load (fallback)
    const timer = window.setTimeout(() => {
      setLoading(false);
    }, 7000);
    
    setLoadingTimeout(timer);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (loadingTimeout !== null) {
        window.clearTimeout(loadingTimeout);
      }
    };
  }, [onComplete, loadingTimeout]);

  return (
    <div className="relative w-full" style={{ height }}>
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
          <p className="text-gray-600 font-medium">Loading document for signing...</p>
          <p className="text-gray-400 text-sm mt-2">Please wait, this may take a moment</p>
        </div>
      )}
      <iframe 
        src={signingUrl}
        className="w-full h-full border-0"
        onLoad={() => {
          // Set a small timeout to allow DocuSeal to fully render
          setTimeout(() => setLoading(false), 1000);
        }}
        title="Document Signing"
        allow="camera"
      />
    </div>
  );
};
